/* Settings-bar controls for the 3D scatter and 3D bubble views.
 *
 * One factory for both. The bubble view gets a size-field picker and the scatter does not, which
 * is the only control that differs - and it is passed in rather than read off the settings,
 * because which of the two charts this is is not something a user changes from the settings bar. */

import { ViewIds } from '@ardoq/api-types';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SettingsType, type SettingsConfig } from '@ardoq/view-settings';
import type { Points3DViewSettings } from '@ardoq/xyz-charts';
import { handleStoreSetting } from 'viewSettings/settingsHelper';

type PointsViewId = ViewIds.SCATTER_3D | ViewIds.BUBBLE_3D;

type NumericField = { name: string; label: string };

type FieldKey =
  | 'selectedFieldNameX'
  | 'selectedFieldNameY'
  | 'selectedFieldNameZ'
  | 'selectedFieldNameRadius';

type FieldDimension = {
  key: FieldKey;
  id: string;
  iconName: IconName;
  i18nKey: string;
  defaultLabel: string;
  /**
   * Whether the picker offers a way back to nothing chosen.
   *
   * Only the size field does. The three positions are required - `resolveXyzFields` returns null
   * without them, so unset is the empty view rather than a chart, and an option that produced it
   * would be a way to turn the chart off. Size is the opposite: the view opens with it unset, the
   * renderer draws uniform marks for it and the accessible name drops the channel, so unset is a
   * state the chart supports and the dropdown could not return to.
   */
  isClearable: boolean;
};

/** The three every mark needs to be placed at all. */
const POSITION_DIMENSIONS: FieldDimension[] = [
  {
    key: 'selectedFieldNameX',
    id: 'points3dFieldX',
    iconName: IconName.ARROW_FORWARD,
    i18nKey: 'views:points3d.menu.xField',
    defaultLabel: 'X axis field',
    isClearable: false,
  },
  {
    key: 'selectedFieldNameY',
    id: 'points3dFieldY',
    iconName: IconName.ARROW_UPWARD,
    i18nKey: 'views:points3d.menu.yField',
    defaultLabel: 'Y axis field',
    isClearable: false,
  },
  {
    key: 'selectedFieldNameZ',
    id: 'points3dFieldZ',
    iconName: IconName.BAR_CHART,
    i18nKey: 'views:points3d.menu.zField',
    defaultLabel: 'Height field',
    isClearable: false,
  },
];

const SIZE_DIMENSION: FieldDimension = {
  key: 'selectedFieldNameRadius',
  id: 'points3dFieldRadius',
  iconName: IconName.CIRCLE,
  i18nKey: 'views:points3d.menu.radiusField',
  defaultLabel: 'Size field',
  isClearable: true,
};

const fieldPicker = (
  viewId: PointsViewId,
  { key, id, iconName, i18nKey, defaultLabel, isClearable }: FieldDimension,
  viewSettings: Points3DViewSettings,
  numericFields: NumericField[],
  i18nLocale: I18nLocale
): SettingsConfig => {
  const selected = viewSettings[key];
  const label = t(i18nLocale, i18nKey, defaultLabel);

  return {
    id,
    type: SettingsType.DROPDOWN,
    iconName,
    label,
    buttonText:
      numericFields.find(({ name }) => name === selected)?.label ?? label,
    options: [
      ...(isClearable
        ? [
            {
              name: `${id}_none`,
              label: t(i18nLocale, 'views:points3d.menu.noField', 'None'),
              type: DropdownOptionType.OPTION,
              isActive: selected === null,
              onClick: () => handleStoreSetting(viewId, { [key]: null }),
            },
          ]
        : []),
      ...numericFields.map(({ name, label: fieldLabel }) => ({
        name: `${id}_${name}`,
        label: fieldLabel,
        type: DropdownOptionType.OPTION,
        isActive: selected === name,
        onClick: () => handleStoreSetting(viewId, { [key]: name }),
      })),
    ],
    dropdownSize: DropdownSize.S,
    isDisabled: numericFields.length === 0,
  };
};

const appearanceDropdown = (
  viewId: PointsViewId,
  viewSettings: Points3DViewSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'points3dAppearance',
  type: SettingsType.DROPDOWN,
  iconName: IconName.VISIBILITY,
  label: t(i18nLocale, 'views:points3d.menu.appearance', 'Appearance'),
  options: [
    {
      name: 'points3d_wallProjections',
      label: t(
        i18nLocale,
        'views:points3d.appearance.wallProjections',
        'Projections on the walls'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.showWallProjections,
      onClick: () =>
        handleStoreSetting(viewId, {
          showWallProjections: !viewSettings.showWallProjections,
        }),
    },
  ],
  dropdownSize: DropdownSize.S,
  isKeepOpen: true,
});

export type Points3DViewModifiersArgs = {
  viewId: PointsViewId;
  viewSettings: Points3DViewSettings;
  numericFields: NumericField[];
  i18nLocale: I18nLocale;
  /** Bubble shows a size picker; scatter has nothing for one to drive. */
  showSizeVariation: boolean;
};

export const getPoints3DViewModifiers = ({
  viewId,
  viewSettings,
  numericFields,
  i18nLocale,
  showSizeVariation,
}: Points3DViewModifiersArgs): SettingsConfig[] => {
  /* No color picker. Conditional formatting already colors components by a rule, and it does it
     across every view rather than only this one - so a second, view-local way to color marks is a
     worse version of a thing that already exists. The chart never read the setting either: nothing
     in `Points3DChart` or `buildPoints3DSourceData` looks at `selectedFieldNameColor`, so the
     control was offering a choice with no effect. */
  const dimensions = [
    ...POSITION_DIMENSIONS,
    ...(showSizeVariation ? [SIZE_DIMENSION] : []),
  ];

  return [
    ...dimensions.map(dimension =>
      fieldPicker(viewId, dimension, viewSettings, numericFields, i18nLocale)
    ),
    appearanceDropdown(viewId, viewSettings, i18nLocale),
  ];
};
